import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "aria-labelledby", "children", "className", "disabled", "focusableWhenDisabled", "fullWidth", "icon", "layoutId", "logo", "nativeButton", "startIcon", "startLogo", "onClick", "render", "size", "style", "endIcon", "endLogo", "type", "variant", "xstyle"];
import { c as _c } from "react-compiler-runtime";
import { forwardRef, useContext, useState } from 'react';
import { Button as BaseButton } from '@base-ui/react/button';
import * as stylex from '@stylexjs/stylex';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { focusRingInset, focusRingOutset } from '../../styles/focus-ring';
import { assertElementRender } from '../../utils/assertElementRender';
import { useLayoutConstraints } from '../layout/LayoutConstraints';
import { filterDefinedProps } from '../../utils/filterDefinedProps';
import { warnButtonContentConflicts } from './warnButtonContentConflicts';
import { warnMutuallyExclusive } from '../../utils/warnMutuallyExclusive';
import { SlotWrapper } from '../shared/SlotWrapper';
import { ButtonGroupDisabledContext, ButtonGroupSizeContext } from '../ButtonGroup/ButtonGroupContext';
import { TextRoot } from '../Text/primitives';
import { createTooltipHandle, TooltipTrigger } from '../Tooltip/primitives';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { theme as experimentalTheme } from 'trellis-theming/experimental-theme.stylex';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const COMPONENT_NAME = 'Button';
// TODO(token-typography): These are temporary component-scoped tokens pending UX design review.
// The following still need UX follow-up because no system token exists yet:
//   button-base-transitionDuration
//   button-default-boxShadow-default
//   button-lg-blockSize - no trellisSys component-size-standard-lg token
//   button-lg-fontSize - no trellisSys 16px font size token
//   button-lg-inlineSize-iconOnly - no trellisSys component-size-standard-lg token
//   button-lg-paddingInline - no button-container-paddingInline-lg token
const containerBaseStyles = {
  default: {
    alignItems: "x6s0dn4",
    borderStyle: "x10trpm",
    borderWidth: "xmhl9si",
    cursor: "x1ypdohk x6hbq5z",
    display: "x3nfvp2",
    flexShrink: "x2lah0s",
    fontFamily: "x8ft0lh",
    fontSize: "x1rai6o4",
    fontWeight: "xf25cwd",
    gap: "x9ifctm",
    justifyContent: "xl56j7k",
    overflow: "xb3r6kr",
    padding: "x1i6jvaa",
    transitionDuration: "xx6bhzk x12w9bfk",
    transitionProperty: "xgzoi8n",
    userSelect: "x87ps6o",
    whiteSpace: "xuxw1ft",
    $$css: true
  }
};
const containerSizeStyles = {
  lg: {
    height: "xn3w4p2",
    borderRadius: "x1sq5fu9",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    $$css: true
  },
  md: {
    height: "x1is74mz",
    borderRadius: "x1sq5fu9",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    $$css: true
  },
  sm: {
    height: "x1t6vu2y",
    borderRadius: "x1sq5fu9",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    $$css: true
  },
  xs: {
    height: "x15zhq6g",
    borderRadius: "x1sq5fu9",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    $$css: true
  }
};
const containerLayoutTextStyles = {
  lg: {
    fontSize: "x1j61zf2",
    paddingInline: "x1u0wxde",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  md: {
    paddingInline: "xlozr3s",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  sm: {
    paddingInline: "x1fa7feh",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  xs: {
    paddingInline: "xs4n4zx",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  }
};
const containerLayoutIconStyles = {
  lg: {
    width: "x187nhsf",
    paddingInline: "x1e6qocp",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  md: {
    width: "xmn0uq4",
    paddingInline: "x1e6qocp",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  sm: {
    width: "xdcih3v",
    paddingInline: "x1e6qocp",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  xs: {
    width: "x11lf007",
    paddingInline: "x1e6qocp",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  }
};
const containerFullWidthStyles = {
  default: {
    width: "xh8yej3",
    $$css: true
  }
};
const containerVariantStyles = {
  primary: {
    backgroundColor: "x1ndlqdg x1q4gbrd xnmoxht",
    borderColor: "xdo5dcy x1e6j4v9 xagodfg",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    boxShadow: "xv186zg",
    color: "x1f27t3a xeoysc6 x1kwn3f",
    $$css: true
  },
  normal: {
    backgroundColor: "xj2iia x12tplbw x74jbbg",
    borderColor: "x13g3qio x11q78n2 x1xe3ye0",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x195tmjs xqzc8mw xjqupag",
    $$css: true
  },
  subtle: {
    backgroundColor: "x4pi3qa x1xrvrjt xdy8t3b xvp0p4v",
    borderColor: "x1kzgcty x1ue9r38 xsqwgk",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1oh5v0i x1l7xfy5 xq4l99t",
    $$css: true
  },
  "primary-destructive": {
    backgroundColor: "x1vy2iym x1fsay80 x1uqp73n xefyinr",
    borderColor: "xdvhvry x10y84ss x178piqv",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1rvjhxu xykkfvj x1adffvp",
    $$css: true
  },
  "normal-destructive": {
    backgroundColor: "xwmn8wo xr3jah5 x1146g7x x1t5j7al",
    borderColor: "x1kd92vs x1be1aoz x1vor433",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "xg6f48w xpw3yre xuh9hlf",
    $$css: true
  },
  "subtle-destructive": {
    backgroundColor: "x8cv2gj xqmriot x1m016qy xy28ekv",
    borderColor: "x1cusp2c x1cztq3w x154qq66",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1rf2up8 x12bk3nq xggha3r",
    $$css: true
  },
  "subtle-flush": {
    backgroundColor: "x4pi3qa x1xrvrjt xdy8t3b xvp0p4v",
    borderColor: "x1kzgcty x1ue9r38 xsqwgk",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    boxShadow: "x1gnnqk1 xktdcrl xq278ym x1xk3k8f",
    color: "x1oh5v0i x1l7xfy5 xq4l99t",
    $$css: true
  },
  "subtle-destructive-flush": {
    backgroundColor: "x8cv2gj xqmriot x1m016qy xy28ekv",
    borderColor: "x1cusp2c x1cztq3w x154qq66",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    boxShadow: "x1gnnqk1 x18r8e1m x6n6lfi x1xk3k8f",
    color: "x1rf2up8 x12bk3nq xggha3r",
    $$css: true
  },
  unstyled: {
    backgroundColor: "x1gjadoe",
    borderColor: "x1vzo851",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    height: "xt7dq6l",
    borderWidth: "xc342km",
    borderInlineWidth: null,
    borderInlineStartWidth: null,
    borderLeftWidth: null,
    borderInlineEndWidth: null,
    borderRightWidth: null,
    borderBlockWidth: null,
    borderTopWidth: null,
    borderBottomWidth: null,
    color: "x1heor9g",
    width: "x14atkfc",
    paddingInline: "x1e6qocp",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  }
};
const containerVariantDisabledStyles = {
  primary: {
    backgroundColor: "x1cp7dub",
    borderColor: "x1a7ecrd",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x19e6nqn",
    $$css: true
  },
  normal: {
    backgroundColor: "x14ujbha",
    borderColor: "x1vyeaoj",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1imlh4w",
    $$css: true
  },
  subtle: {
    backgroundColor: "x1m6swh",
    borderColor: "x6oln25",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1tzdijr",
    $$css: true
  },
  "primary-destructive": {
    backgroundColor: "xemsd14",
    borderColor: "x1bqjxjf",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1gamul3",
    $$css: true
  },
  "normal-destructive": {
    backgroundColor: "xv21124",
    borderColor: "x16wizdy",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "xdvhmfk",
    $$css: true
  },
  "subtle-destructive": {
    backgroundColor: "x1hjz321",
    borderColor: "x9uw8k6",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "xmf3tyi",
    $$css: true
  },
  "subtle-flush": {
    backgroundColor: "x1m6swh",
    borderColor: "x6oln25",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1tzdijr",
    $$css: true
  },
  "subtle-destructive-flush": {
    backgroundColor: "x1hjz321",
    borderColor: "x9uw8k6",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "xmf3tyi",
    $$css: true
  },
  unstyled: {
    backgroundColor: "x1gjadoe",
    borderColor: "x1vzo851",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    color: "x1heor9g",
    $$css: true
  }
};

// Styles shared across all flush variants, applied conditionally in stylex.props().
// Kept separate from containerVariantStyles because StyleX doesn't allow spreading
// or reuse within a single stylex.create() call.
const containerFlushStyles = {
  base: {
    height: "xt7dq6l",
    borderRadius: "x1sq5fu9",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    overflow: "x1rea2x4",
    overflowX: null,
    overflowY: null,
    paddingInline: "x1e6qocp",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    $$css: true
  },
  disabled: {
    boxShadow: "x1gnnqk1",
    $$css: true
  }
};
const iconSlotStyles = {
  default: {
    alignItems: "x6s0dn4",
    height: "xlzyvqe",
    display: "x78zum5",
    flexShrink: "x2lah0s",
    fontSize: "x11rh9xa",
    width: "x15vhz18",
    justifyContent: "xl56j7k",
    $$css: true
  }
};
const logoSlotStyles = {
  default: {
    color: "x1dwjgk9",
    $$css: true
  }
};
const containerLayoutSizeStyles = {
  icon: {
    lg: containerLayoutIconStyles.lg,
    md: containerLayoutIconStyles.md,
    sm: containerLayoutIconStyles.sm,
    xs: containerLayoutIconStyles.xs
  },
  text: {
    lg: containerLayoutTextStyles.lg,
    md: containerLayoutTextStyles.md,
    sm: containerLayoutTextStyles.sm,
    xs: containerLayoutTextStyles.xs
  }
};

/**
 * Used to perform a non-navigational action. Supports both label text and icons.
 *
 * This is the primitive version. Prefer the config API, which covers most use cases.
 *
 * @see {@link __ButtonConfig | Button}
 */
const Button = /*#__PURE__*/forwardRef((t0, ref) => {
  var _ref, _sizeProp;
  const $ = _c(64);
  let ariaLabel;
  let ariaLabelledBy;
  let children;
  let endIcon;
  let endLogo;
  let focusableWhenDisabled;
  let icon;
  let layoutId;
  let logo;
  let nativeButton;
  let onClick;
  let render;
  let restProps;
  let sizeProp;
  let startIcon;
  let startLogo;
  let t1;
  let t2;
  let t3;
  let t4;
  let xstyle;
  if ($[0] !== t0) {
    const {
        "aria-label": t5,
        "aria-labelledby": t6,
        children: t7,
        disabled: t8,
        focusableWhenDisabled: t9,
        fullWidth: t10,
        icon: t11,
        layoutId: t12,
        logo: t13,
        nativeButton: t14,
        startIcon: t15,
        startLogo: t16,
        onClick: t17,
        render: t18,
        size: t19,
        endIcon: t20,
        endLogo: t21,
        type: t22,
        variant: t23,
        xstyle: t24
      } = t0,
      t25 = _objectWithoutPropertiesLoose(t0, _excluded);
    ariaLabel = t5;
    ariaLabelledBy = t6;
    children = t7;
    t1 = t8;
    focusableWhenDisabled = t9;
    t2 = t10;
    icon = t11;
    layoutId = t12;
    logo = t13;
    nativeButton = t14;
    startIcon = t15;
    startLogo = t16;
    onClick = t17;
    render = t18;
    sizeProp = t19;
    endIcon = t20;
    endLogo = t21;
    t3 = t22;
    t4 = t23;
    xstyle = t24;
    restProps = t25;
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = ariaLabelledBy;
    $[3] = children;
    $[4] = endIcon;
    $[5] = endLogo;
    $[6] = focusableWhenDisabled;
    $[7] = icon;
    $[8] = layoutId;
    $[9] = logo;
    $[10] = nativeButton;
    $[11] = onClick;
    $[12] = render;
    $[13] = restProps;
    $[14] = sizeProp;
    $[15] = startIcon;
    $[16] = startLogo;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = xstyle;
  } else {
    ariaLabel = $[1];
    ariaLabelledBy = $[2];
    children = $[3];
    endIcon = $[4];
    endLogo = $[5];
    focusableWhenDisabled = $[6];
    icon = $[7];
    layoutId = $[8];
    logo = $[9];
    nativeButton = $[10];
    onClick = $[11];
    render = $[12];
    restProps = $[13];
    sizeProp = $[14];
    startIcon = $[15];
    startLogo = $[16];
    t1 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    xstyle = $[21];
  }
  const disabledProp = t1 === undefined ? false : t1;
  const fullWidth = t2 === undefined ? false : t2;
  const type = t3 === undefined ? "button" : t3;
  const variant = t4 === undefined ? "normal" : t4;
  assertElementRender("Button", render);
  warnMutuallyExclusive("Button", render, xstyle);
  warnButtonContentConflicts(icon, children, startIcon, endIcon, ariaLabel, ariaLabelledBy, logo, startLogo, endLogo);
  const groupSize = useContext(ButtonGroupSizeContext);
  const groupDisabled = useContext(ButtonGroupDisabledContext);
  const size = (_ref = (_sizeProp = sizeProp) !== null && _sizeProp !== void 0 ? _sizeProp : groupSize) !== null && _ref !== void 0 ? _ref : "md";
  const disabled = disabledProp || groupDisabled;
  const hasChildren = children != null && children !== "";
  const resolvedLayout = icon != null || logo != null ? "icon" : "text";
  const isFlushVariant = variant === "subtle-flush" || variant === "subtle-destructive-flush";
  const isUnstyledVariant = variant === "unstyled";
  const [tooltipHandle] = useState(_temp);
  let t5;
  if ($[22] !== ariaLabel || $[23] !== ariaLabelledBy) {
    t5 = filterDefinedProps({
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy
    });
    $[22] = ariaLabel;
    $[23] = ariaLabelledBy;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const optionalAriaProps = t5;
  let t6;
  if ($[25] !== layoutId) {
    t6 = {
      layoutId
    };
    $[25] = layoutId;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const {
    layoutConstraintStyles
  } = useLayoutConstraints(t6);
  const t7 = render;
  let t8;
  if ($[27] !== disabled || $[28] !== fullWidth || $[29] !== isFlushVariant || $[30] !== isUnstyledVariant || $[31] !== layoutConstraintStyles || $[32] !== render || $[33] !== resolvedLayout || $[34] !== size || $[35] !== variant || $[36] !== xstyle) {
    t8 = stylex.props(render == null && containerBaseStyles.default, render == null && containerSizeStyles[size], render == null && containerLayoutSizeStyles[resolvedLayout][size], render == null && fullWidth && containerFullWidthStyles.default, render == null && containerVariantStyles[variant], render == null && isFlushVariant && containerFlushStyles.base, render == null && disabled && containerVariantDisabledStyles[variant], render == null && disabled && isFlushVariant && containerFlushStyles.disabled, render == null && (isUnstyledVariant ? focusRingOutset.default : focusRingInset.default), render == null && layoutConstraintStyles, render == null && xstyle);
    $[27] = disabled;
    $[28] = fullWidth;
    $[29] = isFlushVariant;
    $[30] = isUnstyledVariant;
    $[31] = layoutConstraintStyles;
    $[32] = render;
    $[33] = resolvedLayout;
    $[34] = size;
    $[35] = variant;
    $[36] = xstyle;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
    };
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== children || $[40] !== endIcon || $[41] !== endLogo || $[42] !== hasChildren || $[43] !== icon || $[44] !== logo || $[45] !== startIcon || $[46] !== startLogo || $[47] !== tooltipHandle) {
    var _logo, _startLogo, _endLogo;
    t10 = logo != null || icon != null ? /*#__PURE__*/_jsx(SlotWrapper, {
      as: "span",
      xstyle: [iconSlotStyles.default, logo != null && logoSlotStyles.default],
      children: (_logo = logo) !== null && _logo !== void 0 ? _logo : icon
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SlotWrapper, {
        as: "span",
        xstyle: [iconSlotStyles.default, startLogo != null && logoSlotStyles.default],
        children: (_startLogo = startLogo) !== null && _startLogo !== void 0 ? _startLogo : startIcon
      }), hasChildren && /*#__PURE__*/_jsx(TextRoot, {
        truncated: true,
        tooltipHandle: tooltipHandle,
        children: children
      }), /*#__PURE__*/_jsx(SlotWrapper, {
        as: "span",
        xstyle: [iconSlotStyles.default, endLogo != null && logoSlotStyles.default],
        children: (_endLogo = endLogo) !== null && _endLogo !== void 0 ? _endLogo : endIcon
      })]
    });
    $[39] = children;
    $[40] = endIcon;
    $[41] = endLogo;
    $[42] = hasChildren;
    $[43] = icon;
    $[44] = logo;
    $[45] = startIcon;
    $[46] = startLogo;
    $[47] = tooltipHandle;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== disabled || $[50] !== focusableWhenDisabled || $[51] !== nativeButton || $[52] !== onClick || $[53] !== optionalAriaProps || $[54] !== ref || $[55] !== restProps || $[56] !== t10 || $[57] !== t7 || $[58] !== t8 || $[59] !== type) {
    t11 = /*#__PURE__*/_jsx(BaseButton, Object.assign({}, restProps, {
      ref: ref,
      render: t7,
      disabled: disabled,
      focusableWhenDisabled: focusableWhenDisabled,
      nativeButton: nativeButton,
      type: type,
      onClick: onClick
    }, optionalAriaProps, t8, t9, {
      children: t10
    }));
    $[49] = disabled;
    $[50] = focusableWhenDisabled;
    $[51] = nativeButton;
    $[52] = onClick;
    $[53] = optionalAriaProps;
    $[54] = ref;
    $[55] = restProps;
    $[56] = t10;
    $[57] = t7;
    $[58] = t8;
    $[59] = type;
    $[60] = t11;
  } else {
    t11 = $[60];
  }
  const buttonElement = t11;
  if (hasChildren) {
    let t12;
    if ($[61] !== buttonElement || $[62] !== tooltipHandle) {
      t12 = /*#__PURE__*/_jsx(TooltipTrigger, {
        handle: tooltipHandle,
        render: buttonElement
      });
      $[61] = buttonElement;
      $[62] = tooltipHandle;
      $[63] = t12;
    } else {
      t12 = $[63];
    }
    return t12;
  }
  return buttonElement;
});
Button.displayName = COMPONENT_NAME;
export { Button };
function _temp() {
  return createTooltipHandle();
}